import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Valor } from '../../components/Valor';
import { ApiError, api, type AsaasResult } from '../../lib/api';
import { dateBR, today } from '../../lib/format';

/**
 * A conta Asaas.
 *
 * Os honorarios passaram a ser cobrados por ela, e isso muda o que o banco
 * mostra: antes o cliente pagava PIX direto e o extrato trazia o nome dele;
 * agora o banco so ve um repasse vindo do Asaas, sem nome e as vezes juntando
 * varios recebimentos num valor so.
 *
 * Ligando a conta, o honorario volta a ter nome e data proprios — e o repasse
 * para o banco deixa de ser contado como receita nova, porque as duas pontas
 * sao amarradas.
 *
 * A chave vai cifrada para o banco de dados e nunca volta para esta tela.
 */
export function Asaas() {
  const cliente = useQueryClient();
  const estado = useQuery({ queryKey: ['asaas'], queryFn: () => api.asaas() });

  const [token, setToken] = useState('');
  const [desde, setDesde] = useState(today());
  const [erro, setErro] = useState<string | null>(null);
  const [ultimo, setUltimo] = useState<AsaasResult | null>(null);

  const atualizar = () => {
    void cliente.invalidateQueries({ queryKey: ['asaas'] });
    void cliente.invalidateQueries({ queryKey: ['accounts'] });
    void cliente.invalidateQueries({ queryKey: ['bancos'] });
    void cliente.invalidateQueries({ queryKey: ['transactions'] });
    void cliente.invalidateQueries({ queryKey: ['dashboard'] });
  };

  const ligar = useMutation({
    mutationFn: () => api.conectarAsaas(token.trim(), desde),
    onSuccess: () => {
      setToken('');
      setErro(null);
      atualizar();
    },
    onError: (falha) => {
      setErro(falha instanceof ApiError ? falha.message : 'Nao consegui ligar a conta.');
    },
  });

  const ler = useMutation({
    mutationFn: () => api.sincronizarAsaas(),
    onSuccess: (resultado) => {
      setUltimo(resultado);
      atualizar();
    },
  });

  const desligar = useMutation({
    mutationFn: () => api.desconectarAsaas(),
    onSuccess: () => {
      setUltimo(null);
      atualizar();
    },
  });

  if (!estado.data) return null;

  if (!estado.data.conectado) {
    return (
      <section className="card" style={{ marginTop: 'var(--space-6)' }}>
        <h2 className="card-title">Conta Asaas</h2>
        <p className="card-hint">
          O Asaas vira uma conta deste sistema: o honorario entra como receita no dia em que o
          cliente paga, e o repasse para o banco deixa de contar como entrada nova — senao o mesmo
          dinheiro apareceria duas vezes. A chave e guardada cifrada e nunca volta para esta tela.
        </p>

        <div className="row">
          <div className="field" style={{ flex: 2, minWidth: 260 }}>
            <label htmlFor="asaasToken">Chave de API do Asaas</label>
            <input
              id="asaasToken"
              type="password"
              value={token}
              onChange={(evento) => setToken(evento.target.value)}
              placeholder="$aact_..."
              autoComplete="off"
            />
            <span className="field-hint">
              No painel do Asaas: Integracoes, Chave de API. Ela e conferida antes de ser guardada.
            </span>
          </div>
          <div className="field" style={{ minWidth: 160 }}>
            <label htmlFor="asaasDesde">Ler a partir de</label>
            <input
              id="asaasDesde"
              type="date"
              value={desde}
              onChange={(evento) => setDesde(evento.target.value)}
            />
          </div>
        </div>

        {erro && <p className="card-hint">{erro}</p>}

        <button
          className="btn"
          disabled={token.trim().length < 20 || ligar.isPending}
          onClick={() => ligar.mutate()}
        >
          {ligar.isPending ? 'Conferindo a chave...' : 'Ligar'}
        </button>
      </section>
    );
  }

  const diferenca = ultimo ? BigInt(ultimo.diferencaCents) : 0n;

  return (
    <section className="card" style={{ marginTop: 'var(--space-6)' }}>
      <div className="between">
        <h2 className="card-title" style={{ marginBottom: 0 }}>
          Conta Asaas
        </h2>
        <div className="row">
          <button className="btn ghost" disabled={ler.isPending} onClick={() => ler.mutate()}>
            {ler.isPending ? 'Lendo...' : 'Ler agora'}
          </button>
          <button className="btn ghost" onClick={() => desligar.mutate()}>
            Desligar
          </button>
        </div>
      </div>

      <p className="card-hint">
        Lendo desde {estado.data.startDate ? dateBR(estado.data.startDate) : '—'}. A leitura tambem
        acontece sozinha ao fim de cada varredura do banco. Os lancamentos ficam mesmo que a leitura
        seja desligada.
      </p>

      {ultimo?.erro && <p className="card-hint">{ultimo.erro}</p>}

      {ultimo && !ultimo.erro && (
        <p className="card-hint" style={{ marginBottom: 0 }}>
          {ultimo.recebimentos} recebimento(s), {ultimo.repasses} repasse(s), {ultimo.pareados}{' '}
          amarrado(s) ao extrato do banco
          {ultimo.baixados > 0 ? `, ${ultimo.baixados} honorario(s) conferido(s)` : ''}.
        </p>
      )}

      {/*
        Diferenca de saldo nao e ajustada em silencio: ajuste silencioso e
        como um numero errado vira permanente. Ela aparece, e quem olha decide.
      */}
      {diferenca !== 0n && (
        <p className="card-hint" style={{ marginBottom: 0 }}>
          O saldo que o Asaas informa esta <Valor cents={ultimo?.diferencaCents ?? '0'} /> diferente
          do apurado aqui. Falta lancamento nesta janela.
        </p>
      )}
    </section>
  );
}
